import React from "react";
import EngineeringIcon from "../EngineeringIcons";
import AnimatedDivider from "../AnimatedDivider";

const EnergyEngineering: React.FC = () => {
  const energySolutions = [
    {
      title: "Pyrolysis Systems",
      description: "Advanced pyrolysis technology for converting waste materials into valuable energy products.",
      icon: "🔥",
      features: ["Waste-to-energy conversion", "Bio-oil production", "Carbon capture technology"]
    },
    {
      title: "Electric Vehicle Systems",
      description: "Electric-powered outboard motors and vehicle conversion systems for sustainable transportation.",
      icon: "🚗",
      features: ["EV conversion kits", "Battery management systems", "Charging infrastructure"]
    },
    {
      title: "Solar Power Solutions",
      description: "Comprehensive solar energy systems for residential, commercial, and industrial applications.",
      icon: "☀️",
      features: ["Solar panel installation", "Energy storage systems", "Grid integration"]
    },
    {
      title: "Wind Energy Systems",
      description: "Wind turbine design and installation for renewable energy generation.",
      icon: "💨",
      features: ["Wind turbine fabrication", "Site assessment", "Maintenance services"]
    }
  ];

  const technologies = [
    "Pyrolysis reactor design and fabrication",
    "Electric motor and drive systems",
    "Battery technology and management",
    "Solar panel manufacturing",
    "Wind turbine engineering",
    "Energy storage solutions"
  ];

  const benefits = [
    {
      title: "Renewable Energy",
      description: "Clean, sustainable energy solutions that reduce carbon footprint",
      icon: "🌱"
    },
    {
      title: "Cost Savings",
      description: "Significant reduction in energy costs and operational expenses",
      icon: "💰"
    },
    {
      title: "Energy Independence",
      description: "Reduce dependence on traditional energy sources",
      icon: "⚡"
    },
    {
      title: "Environmental Impact",
      description: "Minimize environmental impact and promote sustainability",
      icon: "🌍"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-600 to-blue-700 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="animate-spin-slow">
                <EngineeringIcon type="bolt" size="lg" className="text-white" />
              </div>
            </div>
            <h1 className="text-5xl font-bold mb-6">Energy Engineering</h1>
            <p className="text-xl mb-8">
              Revolutionary energy solutions including pyrolysis systems, electric-powered outboard motors, 
              and sustainable vehicle technologies for a greener future.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <span className="bg-white/20 px-4 py-2 rounded-full">Renewable Energy</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">Sustainable Power</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">Green Technology</span>
            </div>
          </div>
        </div>
      </section>

      <AnimatedDivider variant="gears" />

      {/* Energy Solutions */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Energy Solutions</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Cutting-edge energy engineering solutions that harness renewable resources and promote sustainable development.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {energySolutions.map((solution, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-8 engineering-card">
                <div className="flex items-center mb-6">
                  <div className="text-4xl mr-4 animate-pulse">{solution.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-800">{solution.title}</h3>
                </div>
                <p className="text-gray-600 mb-6">{solution.description}</p>
                <ul className="space-y-2">
                  {solution.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-gray-700">
                      <EngineeringIcon type="bolt" size="sm" className="text-blue-500 mr-3" animated={false} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AnimatedDivider variant="tools" />

      {/* Benefits */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Benefits of Our Energy Solutions</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Our energy engineering solutions deliver multiple benefits for your business and the environment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-lg hover:bg-blue-50 transition-colors">
                <div className="text-4xl mb-4 animate-bounce-slow">{benefit.icon}</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AnimatedDivider variant="mixed" />

      {/* Technologies */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Technologies</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Advanced energy engineering technologies designed for efficiency, reliability, and sustainability.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {technologies.map((technology, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center">
                  <EngineeringIcon type="cog" size="md" className="text-blue-500 mr-4" />
                  <span className="font-semibold text-gray-800">{technology}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Power the Future with Sustainable Energy</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join the energy revolution with our innovative renewable energy solutions. 
            Let's build a sustainable future for Ekumfiman powered by clean, efficient energy.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2">
              <EngineeringIcon type="bolt" size="sm" animated={false} />
              Get Energy Assessment
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors flex items-center justify-center gap-2">
              <EngineeringIcon type="gear" size="sm" />
              Learn More
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EnergyEngineering; 